import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";

import "./DietPlansList.scss";
import { useDispatch, useSelector } from "react-redux";

import Badge from "../../../atoms/Badge/Badge";
import Button from "../../../atoms/Button/Button";
import DietPlanPdf from "../../DietPlanPdf/DietPlanPdf";
import { Modal, Tooltip } from "antd";

import {
  DIET_STATUS,
  DIET_STATUS_DROPDOWN,
  DIET_STATUS_DROPDOWN_DEFAULT,
} from "../../../constants/diet";

import { DietPlan } from "../../../types/diet";
import {
  getDietPlansRequest,
  updateDietPlanStatusRequest,
} from "../../../store/slices/DietSlice";
import { formatDateTime } from "../../../utils/date-and-time";
import { RootState } from "../../../store/store";
import { AiOutlineCopy } from "react-icons/ai";
import { RiPencilFill } from "react-icons/ri";
import { toast } from "react-toastify";
import DietDeactivate from "../../../atoms/Diet/DietDeactivate/DietDeactivate";
import useCoachPermissions from "../../../hooks/useCoachPermissions";

import { ReactComponent as AppleIcon } from "../../../assets/icons/patient-layout-v2/overview/AppleIcon.svg";
import { ReactComponent as ArrowRightIcon } from "../../../assets/icons/patient-layout-v2/overview/ArrowRightIcon.svg";

const SKELETON_ROWS = [1, 2, 3];

interface Props {
  handleAddDietClick: () => void;
  handleEditDietClick: (dietPlan: DietPlan) => void;
  handleCopyDietClick: (dietPlan: DietPlan) => void;
}

const DietPlansList = forwardRef<any, Props>(
  ({ handleAddDietClick, handleEditDietClick, handleCopyDietClick }, ref) => {
    const { dietPlans } = useSelector((state: RootState) => state.diet);
    const { patientDetails } = useSelector((state: RootState) => state.patient);
    const { healthCoachDetails } = useSelector(
      (state: RootState) => state.healthCoach,
    );
    const dispatch = useDispatch();
    // Every coach can read the plans; only the diet coach (or a chief) creates,
    // copies, edits or deactivates them.
    const { canManageDiet } = useCoachPermissions();
    const [dietType, setDiettype] = useState(DIET_STATUS_DROPDOWN_DEFAULT);

    const [dietPlansVisible, setDietPlansVisible] = useState<any>([]);
    const [showViewAllBtn, setShowViewAllBtn] = useState(false);
    const [toggleAllPlans, setToggleAllPlans] = useState(false);
    const [selectedDietData, setSelectedDietData] = useState<any>(null);
    const [showDietPdfModal, setShowDietPdfModal] = useState(false);
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);

    const handleChange = (value: any) => {
      setDiettype(value);
      setToggleAllPlans(false);
    };

    useEffect(() => {
      if (dietType && patientDetails?.data?.patient_id) {
        getDietPlans();
      }
    }, [dietType, patientDetails]);

    useEffect(() => {
      if (dietPlans?.data) {
        setDietPlansVisible(dietPlans?.data ? dietPlans.data.slice(0, 2) : []);
        setShowViewAllBtn(dietPlans?.data?.length > 2);
      }
    }, [dietPlans?.data]);

    const getDietPlans = () => {
      const payload = {
        diet_type: dietType,
        patient_id: patientDetails?.data?.patient_id,
      };

      new Promise((resolve, reject) => {
        dispatch(
          getDietPlansRequest({
            payload,
            resolve,
            reject,
          }),
        );
      });
    };

    useImperativeHandle(ref, () => ({
      refresh: getDietPlans,
    }));

    const getStatus = (status: string | null) => {
      switch (status) {
        case DIET_STATUS.PENDING:
          return "Pending";
        case DIET_STATUS.APPROVED:
          return "Approved";
        case DIET_STATUS.DRAFT:
          return "Draft";
        case DIET_STATUS.INACTIVE:
          return "Inactive";
        default:
          return "Rejected";
      }
    };

    const toogleAllPlansFn = (flag: any) => {
      setToggleAllPlans(flag);
      if (flag) {
        setDietPlansVisible(dietPlans?.data);
      } else {
        setDietPlansVisible(dietPlans?.data ? dietPlans.data.slice(0, 2) : []);
      }
    };

    //function to Copy Diet
    const copyDietPlan = async (data: any) => {
      // diet_plan_id: data?.diet_plans_id
      const dietPlan = {
        ...data,
        diet_plan_meal_rel: data?.diet_plan_meal_rel?.map((meal: any) => {
          return {
            ...meal,
            meal_options: meal.meal_options.map((option: any) => {
              return {
                ...option,
                option_data: option.option_data
                  .filter(
                    (item: any) =>
                      // item.hide_meal === "N" &&
                      item.is_food_item_added_by_patient === "N" ||
                      !item.hasOwnProperty("is_food_item_added_by_patient"),
                  )
                  .map((item: any) => {
                    return {
                      ...item,
                      // Preserve the tips field from the original data
                      tips: item.tips || "",
                    };
                  }),
              };
            }),
          };
        }),
      };
      handleCopyDietClick(dietPlan);
    };

    const handleUpdateStatus = async (dietPlanId: any) => {
      const payload = {
        dietPlanId,
        status: DIET_STATUS.INACTIVE,
      };
      new Promise((resolve, reject) => {
        dispatch(
          updateDietPlanStatusRequest({
            payload,
            resolve,
            reject,
          }),
        );
      })
        .then((response: any) => {
          toast.success("Diet plan deactivated successfully");
          getDietPlans();
        })
        .catch((error: any) => {
          toast.error(error?.message || "Failed to deactivate diet plan");
        });
      setSelectedDietData(null);
      setShowDeactivateModal(false);
    };

    const isTodayWithinRange = (
      startDate: string | null,
      endDate: string | null,
    ): boolean => {
      if (!startDate || !endDate) return false; // Handle null dates
      const today = new Date().setHours(0, 0, 0, 0); // Clear time portion
      const end = new Date(endDate).setHours(0, 0, 0, 0);
      return today <= end;
    };

    return (
      <div className="diet-list-container">
        <div className="diet-status-chips">
          {DIET_STATUS_DROPDOWN.map((option) => (
            <Button
              key={option.value}
              className="btn-chip"
              variant={dietType === option.value ? "contained" : "outlined"}
              onClick={() => handleChange(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </div>

        <div className="diet-cards">
          {dietPlans?.isLoading &&
            SKELETON_ROWS.map((row) => (
              <div className="diet-card diet-card-skeleton" key={row}>
                <div className="skeleton-block skeleton-ico" />
                <div className="diet-card-main">
                  <div className="skeleton-block skeleton-line skeleton-line-name" />
                  <div className="skeleton-block skeleton-line skeleton-line-meta" />
                </div>
                <div className="skeleton-block skeleton-line skeleton-line-btn" />
              </div>
            ))}

          {!dietPlans?.isLoading && dietPlansVisible?.length === 0 && (
            <div className="diet-cards-empty">No plans available</div>
          )}

          {!dietPlans?.isLoading &&
            dietPlansVisible?.map((dietPlan: DietPlan) => (
              <div className="diet-card" key={dietPlan.diet_plans_id}>
                <div className="diet-card-ico">
                  <AppleIcon />
                </div>

                <div className="diet-card-main">
                  <div
                    className="diet-card-name"
                    title={dietPlan?.plan_name || undefined}
                  >
                    {dietPlan?.plan_name}
                    <Badge
                      className="plan-status-badge"
                      variant={
                        dietPlan?.status === DIET_STATUS.REJECTED ||
                        dietPlan?.status === DIET_STATUS.INACTIVE
                          ? "danger"
                          : dietPlan?.status === DIET_STATUS.APPROVED
                            ? "success"
                            : "warning"
                      }
                    >
                      {getStatus(dietPlan?.status)}
                    </Badge>
                  </div>
                  <div className="diet-card-meta">
                    {dietPlan.start_date
                      ? formatDateTime({
                          timestamp: dietPlan.start_date,
                          showTime: false,
                        })
                      : "N/A"}
                    <span className="sep">-</span>
                    {dietPlan.end_date
                      ? formatDateTime({
                          timestamp: dietPlan.end_date,
                          showTime: false,
                        })
                      : "N/A"}
                  </div>
                </div>

                <div className="diet-card-actions">
                  {dietPlan?.status !== DIET_STATUS.PENDING && (
                    <Button
                      variant="outlined"
                      size="sm"
                      iconEnd={<ArrowRightIcon />}
                      onClick={() => {
                        const dietPlanTemp = {
                          ...dietPlan,
                          diet_plan_meal_rel: dietPlan?.diet_plan_meal_rel?.map(
                            (meal: any) => {
                              return {
                                ...meal,
                                meal_options: meal?.meal_options?.map(
                                  (option: any) => {
                                    return {
                                      ...option,
                                      option_data: option?.option_data?.filter(
                                        (item: any) =>
                                          item?.is_food_item_added_by_patient ===
                                            "N" ||
                                          !item?.hasOwnProperty(
                                            "is_food_item_added_by_patient",
                                          ),
                                      ),
                                    };
                                  },
                                ),
                              };
                            },
                          ),
                        };
                        setSelectedDietData(dietPlanTemp);
                        setShowDietPdfModal(true);
                      }}
                    >
                      View PDF
                    </Button>
                  )}

                  {canManageDiet &&
                    dietPlan?.status === DIET_STATUS.APPROVED &&
                    isTodayWithinRange(
                      dietPlan?.start_date,
                      dietPlan?.end_date,
                    ) && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="sm"
                        onClick={() => {
                          setSelectedDietData(dietPlan);
                          setShowDeactivateModal(true);
                        }}
                      >
                        Deactivate
                      </Button>
                    )}

                  {canManageDiet && (
                    <Tooltip placement="topLeft" title={"Click to Copy Diet"}>
                      <Button
                        variant="outlined"
                        size="sm"
                        title="Copy diet plan"
                        onClick={() => copyDietPlan(dietPlan)}
                        iconStart={<AiOutlineCopy />}
                      />
                    </Tooltip>
                  )}

                  {canManageDiet &&
                    (dietPlan?.status === DIET_STATUS.PENDING ||
                      dietPlan?.status === DIET_STATUS.DRAFT) && (
                      <Tooltip
                        placement="topLeft"
                        title={"Click to Change Diet"}
                      >
                        <Button
                          variant="outlined"
                          size="sm"
                          title="Edit diet plan"
                          onClick={() => handleEditDietClick(dietPlan)}
                          iconStart={<RiPencilFill />}
                        />
                      </Tooltip>
                    )}
                </div>
              </div>
            ))}
        </div>

        {showViewAllBtn && (
          <Button
            variant="outlined"
            size="sm"
            rounded="full"
            className="view-all-btn"
            onClick={() => toogleAllPlansFn(!toggleAllPlans)}
          >
            {!toggleAllPlans ? "View All" : "View less"}
          </Button>
        )}

        <Modal
          centered
          open={showDietPdfModal && selectedDietData}
          footer={null}
          onCancel={() => setShowDietPdfModal(false)}
          width={"90%"}
          style={{ height: "500px", marginTop: "20px" }}
          zIndex={10000}
        >
          <div>
            <DietPlanPdf
              dietData={selectedDietData}
              userDetails={healthCoachDetails?.data}
            />
          </div>
        </Modal>

        <Modal
          centered
          open={showDeactivateModal && selectedDietData}
          footer={null}
          onCancel={() => {
            setShowDeactivateModal(false);
            setSelectedDietData(null);
          }}
          width={"60%"}
          style={{ height: "500px", marginTop: "20px" }}
          zIndex={10000}
          title="Deactivate Diet Plan"
        >
          <DietDeactivate
            patientDetails={patientDetails}
            selectedDietData={selectedDietData}
            onConfirm={() =>
              handleUpdateStatus(selectedDietData?.diet_plans_id)
            }
            onCancel={() => {
              setShowDeactivateModal(false);
              setSelectedDietData(null);
            }}
          />
        </Modal>
      </div>
    );
  },
);

export default DietPlansList;
